// client/src/pages/seo/SpeisekarteOnlineAendernDe.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "Eine Restaurant-Speisekarte sollte so einfach aktualisierbar sein, wie es der Alltag erfordert",
  },
  {
    id: "why",
    label: "Warum ist es wichtig, eine Restaurant-Speisekarte online zu ändern?",
  },
  {
    id: "what",
    label: "Was sollte ein Restaurant online ändern können?",
  },
  {
    id: "workflow",
    label: "Wie funktioniert das Ändern einer Speisekarte online in der Praxis?",
  },
  {
    id: "pdf",
    label: "Warum ist eine PDF bei häufigen Menüänderungen oft unpraktisch?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren, wenn Restaurants Speisekarten online aktualisieren?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich das als Restaurantinhaber angehen?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place hilft, Ihre Speisekarte online zu aktualisieren",
  },
  {
    id: "faq",
    label: "FAQ zum Online-Ändern einer Restaurant-Speisekarte",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function SpeisekarteOnlineAendernDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Speisekarte online ändern: Preise und Gerichte selbst aktualisieren | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/change-menu-online",
          de: "/de/speisekarte-online-aendern",
          no: "/no/endre-meny-online",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Speisekarte online ändern: Preise, Gerichte und Fotos selbst
              aktualisieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                In einem Restaurant ändert sich ständig etwas: Ein Gericht ist
                ausverkauft, ein Preis muss angepasst werden, ein Tagesangebot
                kommt hinzu oder ein besseres Foto ist fertig. Genau hier wird
                eine digitale Speisekarte praktisch.
              </p>

              <p className="seoHero__text">
                Statt eine neue PDF zu exportieren, einen Designer zu fragen
                oder die Speisekarte neu zu drucken, kann das Restaurant Inhalte
                online aktualisieren. Der QR-Code bleibt gleich, während die
                Speisekarte dahinter aktuell bleibt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Eine Restaurant-Speisekarte sollte so einfach aktualisierbar sein,
            wie es der Alltag erfordert
          </h2>

          <p>
            Viele Restaurants nutzen noch PDF-Dateien oder gedruckte
            Speisekarten. Das kann funktionieren, wenn sich nur wenig ändert.
            Wenn Preise, Verfügbarkeit, Tagesangebote oder Fotos regelmäßig
            angepasst werden, wird eine statische Speisekarte aber schnell
            unpraktisch.
          </p>

          <p>
            Eine digitale Speisekarte ist nützlich, weil das Restaurant Inhalte
            online ändern kann, ohne den QR-Code zu verändern. Dadurch werden
            alltägliche Aktualisierungen schneller, einfacher und weniger
            abhängig von Designern, Druckereien oder technischer Hilfe.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Langsamer Ablauf</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Änderung notieren.</li>
                <li className="text__seo-li">
                  Designer oder Teammitglied fragen.
                </li>
                <li className="text__seo-li">Neue PDF erstellen.</li>
                <li className="text__seo-li">Datei erneut hochladen.</li>
                <li className="text__seo-li">QR-Code-Link prüfen.</li>
                <li className="text__seo-li">
                  Gäste sehen möglicherweise weiterhin alte Informationen.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besserer Ablauf</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Admin-Bereich öffnen.</li>
                <li className="text__seo-li">
                  Gericht, Preis oder Foto bearbeiten.
                </li>
                <li className="text__seo-li">Änderungen speichern.</li>
                <li className="text__seo-li">
                  Die digitale Speisekarte ist aktualisiert.
                </li>
                <li className="text__seo-li">Der QR-Code bleibt gleich.</li>
              </ul>
            </div>
          </div>

          <h3>Zuerst eine kleine Änderung testen</h3>

          <p>
            Fügen Sie einige echte Produkte hinzu, ändern Sie einen Preis und
            öffnen Sie die Speisekarte über den QR-Code auf einem Smartphone.
            Dieser kleine Test zeigt schnell, ob Online-Menübearbeitung den
            Alltag erleichtern kann.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum ist es wichtig, eine Restaurant-Speisekarte online zu ändern?
            </h2>

            <p>
              Eine Speisekarte ist kein statisches Dokument. Sie zeigt nicht nur,
              was ein Restaurant anbietet, sondern auch, ob die Informationen für
              Gäste aktuell und zuverlässig sind.
            </p>

            <p>
              Wenn ein Gast online ein Gericht sieht, das nicht mehr verfügbar
              ist, entsteht Enttäuschung. Wenn Preise veraltet sind, kann das zu
              unangenehmen Gesprächen führen. Wenn Tagesangebote nicht sichtbar
              sind, gehen Chancen verloren.
            </p>

            <h3>Online-Aktualisierungen unterstützen den Restaurantalltag</h3>

            <p>
              Restaurant-Technologie ist dann am nützlichsten, wenn sie
              praktische Probleme löst: schnellere Aktualisierungen, weniger
              veraltete Speisekarten, klarere Informationen für Gäste und
              weniger Abhängigkeit von Druckmaterial.
            </p>

            <p>
              Wenn Gäste digital suchen, QR-Codes scannen und Speisekarten auf
              dem Smartphone öffnen, sollten Menüinformationen auch online leicht
              aktuell gehalten werden können.
            </p>
          </div>
        </section>

        <section id="what" className="acquaintance">
          <h2>Was sollte ein Restaurant online ändern können?</h2>

          <p>
            Eine gute digitale Speisekarte sollte nicht nur anzeigen, was einmal
            eingetragen wurde. Sie sollte dem Inhaber oder Team ermöglichen, die
            wichtigsten Inhalte direkt zu verwalten.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Preise ändern.</li>
            <li className="text__seo-li">Gerichte hinzufügen.</li>
            <li className="text__seo-li">Produkte ausblenden.</li>
            <li className="text__seo-li">Tagesangebote veröffentlichen.</li>
            <li className="text__seo-li">Fotos ersetzen.</li>
            <li className="text__seo-li">Beschreibungen verbessern.</li>
            <li className="text__seo-li">Kategorien sortieren.</li>
            <li className="text__seo-li">Verfügbarkeit anpassen.</li>
          </ul>

          <p>
            Die Möglichkeit, Produkte vorübergehend auszublenden, ist besonders
            wichtig. Wenn ein Gericht ausverkauft ist, sollte es nicht weiterhin
            so sichtbar bleiben, als wäre es verfügbar.
          </p>

          <VisualIdea>
            Eine Admin-Oberfläche mit drei einfachen Aktionen: Preis ändern,
            Gericht ausblenden, Foto ersetzen. Daneben ein Smartphone mit der
            aktualisierten Speisekarte.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Wie funktioniert das Ändern einer Speisekarte online in der Praxis?
            </h2>

            <p>
              Der Ablauf sollte so einfach sein, dass nicht für jede kleine
              Änderung externe Hilfe benötigt wird. Ein Restaurantinhaber,
              Manager oder Teammitglied sollte typische Aktualisierungen selbst
              erledigen können.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Einloggen:</strong> Der Inhaber öffnet den
                Admin-Bereich.
              </li>

              <li className="text__seo-li">
                <strong>Eintrag auswählen:</strong> Gericht, Getränk oder
                Tagesangebot auswählen.
              </li>

              <li className="text__seo-li">
                <strong>Inhalt bearbeiten:</strong> Preis, Beschreibung, Foto
                oder Sichtbarkeit anpassen.
              </li>

              <li className="text__seo-li">
                <strong>Speichern:</strong> Die öffentliche Speisekarte wird
                aktualisiert.
              </li>

              <li className="text__seo-li">
                <strong>QR-Code prüfen:</strong> Der bestehende QR-Code öffnet
                weiterhin die aktualisierte Menüseite.
              </li>
            </ol>

            <p>
              Der wichtigste Punkt: Der QR-Code muss nicht neu gedruckt werden,
              solange der Menü-Link stabil bleibt.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>
            Warum ist eine PDF bei häufigen Menüänderungen oft unpraktisch?
          </h2>

          <p>
            Eine PDF wirkt am Anfang einfach. Man nimmt die vorhandene
            Speisekarte, exportiert sie und verknüpft sie mit einem QR-Code. Bei
            seltenen Änderungen kann das ausreichen.
          </p>

          <p>
            Schwierig wird es, wenn sich die Speisekarte regelmäßig ändert. Jede
            Aktualisierung erfordert Bearbeiten, Exportieren, Hochladen und
            erneutes Testen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF-Speisekarte</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Auf kleinen Bildschirmen oft schwer lesbar.
                </li>
                <li className="text__seo-li">
                  Änderungen brauchen mehrere Schritte.
                </li>
                <li className="text__seo-li">
                  Alte Versionen können weiter im Umlauf bleiben.
                </li>
                <li className="text__seo-li">
                  Für Tagesangebote nicht ideal.
                </li>
                <li className="text__seo-li">
                  Keine flexible Produktverwaltung.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Online verwaltete Speisekarte</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Für Smartphones strukturiert.</li>
                <li className="text__seo-li">
                  Preise können direkt bearbeitet werden.
                </li>
                <li className="text__seo-li">
                  Ausverkaufte Artikel können ausgeblendet werden.
                </li>
                <li className="text__seo-li">
                  Fotos und Kategorien lassen sich einfacher verwalten.
                </li>
                <li className="text__seo-li">QR-Code kann gleich bleiben.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Welche Fehler passieren, wenn Restaurants Speisekarten online
              aktualisieren?
            </h2>

            <p>
              Online-Aktualisierungen helfen nur, wenn sie klar organisiert
              sind. Sonst kann auch eine digitale Speisekarte unübersichtlich
              werden.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Preise werden online und offline nicht gleichzeitig
                    aktualisiert.
                  </li>
                  <li className="text__seo-li">
                    Ausverkaufte Gerichte bleiben sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Mehrere PDF-Versionen sind im Umlauf.
                  </li>
                  <li className="text__seo-li">
                    Fotos werden ohne einheitlichen Stil hochgeladen.
                  </li>
                  <li className="text__seo-li">
                    Kategorien werden zu lang oder unordentlich.
                  </li>
                  <li className="text__seo-li">
                    Der QR-Code führt zu einer alten Datei.
                  </li>
                  <li className="text__seo-li">
                    Niemand ist für die Menüpflege verantwortlich.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Eine zentrale digitale Speisekarte nutzen.
                  </li>
                  <li className="text__seo-li">
                    Änderungen sofort speichern.
                  </li>
                  <li className="text__seo-li">
                    Nicht verfügbare Produkte vorübergehend ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Fotos bewusst auswählen.
                  </li>
                  <li className="text__seo-li">
                    Kategorien regelmäßig aufräumen.
                  </li>
                  <li className="text__seo-li">
                    Einen stabilen Menü-Link verwenden.
                  </li>
                  <li className="text__seo-li">
                    Eine verantwortliche Person festlegen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich das als Restaurantinhaber angehen?</h2>

          <p>
            Ich würde nicht versuchen, sofort die gesamte Speisekarte perfekt zu
            digitalisieren. Ich würde mit den Bereichen starten, die sich am
            häufigsten ändern.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Mit Tagesangeboten starten:</strong> Der Nutzen wird
              schneller sichtbar als bei einer Speisekarte, die sich selten
              ändert.
            </li>

            <li className="text__seo-li">
              <strong>Preisänderungen testen:</strong> Einen Preis ändern und
              prüfen, ob die Aktualisierung sofort sichtbar ist.
            </li>

            <li className="text__seo-li">
              <strong>Ein Gericht ausblenden:</strong> Simulieren, dass ein
              Produkt ausverkauft ist.
            </li>

            <li className="text__seo-li">
              <strong>Ein Foto ersetzen:</strong> Prüfen, ob Bilder die
              Präsentation verbessern.
            </li>

            <li className="text__seo-li">
              <strong>QR-Code stabil halten:</strong> Denselben QR-Code weiter
              nutzen, während sich Inhalte ändern.
            </li>

            <li className="text__seo-li">
              <strong>Team einbeziehen:</strong> Mindestens eine Person sollte
              wissen, wie Aktualisierungen vorgenommen werden.
            </li>
          </ol>

          <p>
            So wird die digitale Speisekarte im echten Arbeitsalltag nützlich
            und bleibt nicht nur eine schöne Online-Präsentation.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie Ihre Restaurant-Speisekarte online ändern möchten,
              starten Sie mit einem kleinen praktischen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Eine digitale Speisekarte erstellen.</li>
              <li className="text__seo-li">
                10 bis 20 echte Produkte hinzufügen.
              </li>
              <li className="text__seo-li">Einen Preis ändern.</li>
              <li className="text__seo-li">
                Ein Produkt testweise ausblenden.
              </li>
              <li className="text__seo-li">
                Die Speisekarte über den QR-Code öffnen.
              </li>
              <li className="text__seo-li">
                Die Ansicht auf einem Smartphone prüfen.
              </li>
              <li className="text__seo-li">
                Danach entscheiden, ob weitere Kategorien ergänzt werden.
              </li>
            </ul>

            <VisualIdea>
              Eine einfache Admin-Oberfläche, in der der Restaurantinhaber einen
              Preis ändert, einen ausverkauften Artikel ausblendet und die
              aktualisierte Speisekarte sofort auf dem Smartphone prüft.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zum Online-Ändern einer Restaurant-Speisekarte</h2>

          <div className="seoFaq">
            <details>
              <summary>Kann ich Menüpreise selbst online ändern?</summary>
              <p>
                Ja, wenn die Speisekarte als digitale Speisekarte mit
                Admin-Bereich aufgebaut ist. Dann können Preise direkt bearbeitet
                und gespeichert werden.
              </p>
            </details>

            <details>
              <summary>
                Muss ich den QR-Code nach jeder Änderung neu drucken?
              </summary>
              <p>
                Nein, wenn der QR-Code auf einen stabilen Menü-Link führt. Die
                Inhalte der Speisekarte ändern sich, aber der QR-Code bleibt
                gleich.
              </p>
            </details>

            <details>
              <summary>Reicht eine PDF-Speisekarte aus?</summary>
              <p>
                Bei seltenen Änderungen kann eine PDF ausreichen. Wenn Preise,
                Tagesangebote oder Verfügbarkeit häufiger wechseln, ist eine
                online verwaltete Speisekarte praktischer.
              </p>
            </details>

            <details>
              <summary>Kann ich ausverkaufte Gerichte ausblenden?</summary>
              <p>
                Ja. Das ist einer der nützlichsten Vorteile einer digitalen
                Speisekarte. Ein Artikel kann vorübergehend ausgeblendet und
                später wieder angezeigt werden.
              </p>
            </details>

            <details>
              <summary>Wer sollte die Speisekarte pflegen?</summary>
              <p>
                Am besten jemand, der den täglichen Restaurantablauf kennt: der
                Inhaber, Manager oder ein verantwortliches Teammitglied.
              </p>
            </details>

            <details>
              <summary>Kann ich nur mit einem Teil der Speisekarte starten?</summary>
              <p>
                Ja. Eine Kategorie, einige echte Produkte und ein schneller
                QR-Code-Test reichen für einen sinnvollen ersten Schritt.
              </p>
            </details>
          </div>
        </section>
        <section className="articls qrArticle__related">
          <h2>Weitere Themen zur digitalen Speisekarte</h2>

          <div className="seoCompareSmall">
            <p>
              <Link to="/de/digitale-speisekarte">
                Digitale Speisekarte für Restaurants
              </Link>
            </p>

            <p>
              <Link to="/de/qr-code-speisekarte">
                QR-Code Speisekarte erstellen
              </Link>
            </p>

            <p>
              <Link to="/de/digitale-speisekarte-kostenlos-testen">
                Digitale Speisekarte kostenlos testen
              </Link>
            </p>

            <p>
              <Link to="/de/mehrsprachige-speisekarte">
                Mehrsprachige Speisekarte für Restaurants
              </Link>
            </p>

            <p>
              <Link to="/de/qr-code-speisekarte-drucken">
                Drucken von QR-Codes für Restaurantmenüs
              </Link>
            </p>

            <p>
              <Link to="/de/tageskarte-digital-erstellen">
                Täglich aktualisierte Sonderangebote
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}